import { useEffect, useState } from "react";
import { User, Wrench, KeyRound } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const DOW = ["DOM", "LUN", "MAR", "MER", "GIO", "VEN", "SAB"];
const STYLE = {
  rental: "bg-red-600/90 border-red-400 text-white", maintenance: "bg-sky-700/90 border-sky-400 text-white",
  confermata: "bg-zinc-500/70 border-zinc-300 text-white", in_attesa: "bg-orange-500/70 border-orange-300 text-white",
  bozza: "bg-zinc-700/80 border-zinc-500 text-zinc-100", completata: "bg-emerald-700/70 border-emerald-400 text-white",
};
const short = (s) => `${parseInt(s.slice(8, 10), 10)}/${parseInt(s.slice(5, 7), 10)}`;

export default function Timeline({ days, vehicles, items, onOpen, onDrop, canDrag }) {
  const [mob, setMob] = useState(window.innerWidth < 640);
  useEffect(() => { const f = () => setMob(window.innerWidth < 640); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  const keys = days.map(ymd);
  const today = ymd(new Date());
  const first = keys[0], last = keys[keys.length - 1];
  const colW = mob ? Math.max(14, Math.floor((window.innerWidth - 190) / Math.min(days.length, 14))) : days.length > 14 ? 56 : days.length > 7 ? 90 : 130;
  const grid = { gridTemplateColumns: `${mob ? 88 : 220}px repeat(${days.length}, minmax(${colW}px, 1fr))` };
  const compact = mob || days.length > 14;
  return (
    <div className="bg-card border border-zinc-800 rounded-xl overflow-x-auto scroll-thin" data-testid="calendar-planning">
      <div className="min-w-max">
        <div className="grid sticky top-0 z-10 bg-black border-b border-zinc-800" style={grid}>
          <div className="px-2 sm:px-4 py-3 text-xs uppercase tracking-wider text-zinc-400 sticky left-0 bg-black z-10">Veicolo</div>
          {days.map((d, i) => (
            <div key={i} className={`text-center py-2 border-l border-zinc-800/70 ${keys[i] === today ? "bg-red-600/20 text-white" : "text-zinc-400"}`}>
              {mob ? <><div className="text-[9px] font-semibold">{DOW[d.getDay()][0]}</div><div className="text-[10px]">{d.getDate()}</div></>
                : <><div className="text-[11px] font-semibold">{DOW[d.getDay()]}</div><div className="text-[11px]">{d.getDate()} {d.toLocaleDateString("it-IT", { month: "short" })}</div></>}
            </div>
          ))}
        </div>
        {vehicles.length === 0 && <div className="text-center text-zinc-500 py-12 text-sm">Nessun veicolo</div>}
        {vehicles.map((v) => {
          const rowItems = items.filter((it) => it.vehicle_id === v.id && it.start.slice(0, 10) <= last && it.end.slice(0, 10) >= first);
          const busy = new Set();
          rowItems.forEach((it) => keys.forEach((k) => { if (it.start.slice(0, 10) <= k && it.end.slice(0, 10) >= k) busy.add(k); }));
          return (
            <div key={v.id} className="grid border-b border-zinc-800/70 relative min-h-[60px]" style={grid} data-testid={`planning-row-${v.id}`}>
              <div className="px-2 sm:px-3 py-2 flex items-center sticky left-0 bg-card z-[5] border-r border-zinc-800/70" style={{ gridColumn: 1, gridRow: 1 }}>
                <div className="min-w-0"><div className="text-xs sm:text-sm font-medium truncate">{v.brand} {v.model}</div><div className="text-[10px] sm:text-[11px] font-mono text-zinc-500">{v.plate}</div></div>
              </div>
              {keys.map((k, i) => (
                <div key={k} style={{ gridColumn: i + 2, gridRow: 1 }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(k, e)}
                  className={`border-l border-zinc-800/50 grid place-items-center ${k === today ? "bg-red-600/5" : ""}`} data-testid={`planning-cell-${v.plate}-${k}`}>
                  {!busy.has(k) && <div className="flex flex-col items-center gap-0.5" title="Disponibile"><span className="h-2 w-2 rounded-full bg-emerald-500" />{!compact && <span className="text-[10px] text-emerald-400/80">Disponibile</span>}</div>}
                </div>
              ))}
              {rowItems.map((it) => {
                const s = Math.max(keys.indexOf(it.start.slice(0, 10)), it.start.slice(0, 10) < first ? 0 : -1);
                const eIdx = it.end.slice(0, 10) > last ? keys.length - 1 : keys.indexOf(it.end.slice(0, 10));
                if (s < 0 || eIdx < 0) return null;
                const Icon = it.kind === "maintenance" ? Wrench : it.kind === "rental" ? KeyRound : User;
                return (
                  <button key={`${it.kind}-${it.id}`} type="button" draggable={canDrag && it.kind === "booking"} onDragStart={(e) => e.dataTransfer.setData("text/plain", it.id)}
                    onClick={() => onOpen(it)} style={{ gridColumn: `${s + 2} / ${eIdx + 3}`, gridRow: 1 }} data-testid={`planning-bar-${it.id}`}
                    className={`relative z-[4] ${mob ? "mx-0.5 px-1" : "mx-1 px-2"} my-2 h-11 rounded-md border-l-4 flex items-center gap-2 text-left overflow-hidden hover:brightness-110 transition ${STYLE[it.kind === "booking" ? it.status : it.kind] || STYLE.bozza}`}>
                    {!mob && <Icon size={15} className="shrink-0 opacity-90" />}
                    <span className="min-w-0 leading-tight"><span className="block text-xs font-semibold truncate">{it.title}</span>
                      <span className="block text-[10px] opacity-80 truncate">{short(it.start)} – {short(it.end)}</span></span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-5 px-4 py-3 text-xs text-zinc-400 border-t border-zinc-800">
        {[["bg-red-600", "Noleggio in corso"], ["bg-zinc-400", "Prenotazione confermata"], ["bg-orange-500", "In attesa"], ["bg-emerald-500", "Disponibile"], ["bg-sky-600", "In manutenzione"]].map(([c, l]) => (
          <span key={l} className="flex items-center gap-2"><span className={`h-3 w-3 rounded-full ${c}`} />{l}</span>))}
      </div>
    </div>
  );
}
